import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { GET_CLIENTS, GET_CATALOGS, CREATE_CLIENT, UPDATE_CLIENT, DELETE_CLIENT, DELETE_CLIENTS } from '../graphql/queries';
import {
  Plus,
  Search,
  X,
  Edit2,
  Trash2,
  Folder
} from 'lucide-react';


import { Client, Catalog } from '../types';

function Clients() {
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    country: '',
    industry: '',
    status: 'Prospect'
  });

  const limit = 10;

  const { data, loading, refetch } = useQuery<{ clients: { clients: Client[], totalPages: number, total: number } }>(GET_CLIENTS, {
    variables: {
      status: statusFilter || undefined,
      search: searchQuery || undefined,
      page,
      limit
    }
  });

  const { data: industriesData } = useQuery<{ catalogs: Catalog[] }>(GET_CATALOGS, { variables: { type: 'clientIndustry' } });
  const { data: statusesData } = useQuery<{ catalogs: Catalog[] }>(GET_CATALOGS, { variables: { type: 'clientStatus' } });

  const [createClient] = useMutation(CREATE_CLIENT, {
    onCompleted: () => {
      closeModal();
      refetch();
    }
  });

  const [updateClient] = useMutation(UPDATE_CLIENT, {
    onCompleted: () => {
      closeModal();
      refetch();
    }
  });

  const [deleteClient] = useMutation(DELETE_CLIENT, {
    onCompleted: () => refetch()
  });

  const [deleteClients] = useMutation(DELETE_CLIENTS, {
    onCompleted: () => {
      setSelectedIds([]);
      refetch();
    }
  });

  const openCreateModal = () => {
    setEditingClient(null);
    setFormData({
      name: '',
      email: '',
      company: '',
      country: '',
      industry: '',
      status: 'Prospect'
    });
    setShowModal(true);
  };

  const openEditModal = (client: Client) => {
    setEditingClient(client);
    setFormData({
      name: client.name,
      email: client.email || '',
      company: client.company,
      country: client.country || '',
      industry: client.industry || '',
      status: client.status
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingClient(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingClient) {
      updateClient({ variables: { id: editingClient.id, input: formData } });
    } else {
      createClient({ variables: { input: formData } });
    }
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this client?')) {
      deleteClient({ variables: { id } });
    }
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedIds(clients.map(c => c.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id: string, checked: boolean) => {
    if (checked) {
      setSelectedIds(prev => [...prev, id]);
    } else {
      setSelectedIds(prev => prev.filter(selectedId => selectedId !== id));
    }
  };

  const handleBulkDelete = () => {
    if (window.confirm(`Are you sure you want to delete ${selectedIds.length} clients?`)) {
      deleteClients({ variables: { ids: selectedIds } });
    }
  };

  const getInitials = (name?: string) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '??';
  };

  const getAvatarColor = (name?: string) => {
    const colors = ['#6366f1', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#ef4444'];
    const index = name?.charCodeAt(0) ? name.charCodeAt(0) % colors.length : 0;
    return colors[index];
  };

  const getStatusColor = (status: string) => {
    const found = statusesData?.catalogs?.find(
      c => c.name.toLowerCase() === status.toLowerCase()
    );
    return found?.color || '#64748B';
  };

  const statusTabs = ['All', 'Active', 'Prospect', 'On Hold', 'Archived'];
  const clients = data?.clients?.clients || [];
  const totalPages = data?.clients?.totalPages || 1;
  const total = data?.clients?.total || 0;


  return (
    <>
      <div className="page-header">
        <h1 className="page-title">Clients</h1>
        <div style={{ display: 'flex', gap: '8px' }}>
          {selectedIds.length > 0 && (
            <button className="btn btn-danger" onClick={handleBulkDelete}>
              <Trash2 size={18} />
              Delete Selected ({selectedIds.length})
            </button>
          )}
          <button className="btn btn-primary" onClick={openCreateModal}>
            <Plus size={18} />
            New client
          </button>
        </div>
      </div>

      <div className="page-content">
        <div className="filters-bar">
          <div className="filter-tabs">
            {statusTabs.map(status => (
              <button
                key={status}
                className={`filter-tab ${(status === 'All' && !statusFilter) || status === statusFilter ? 'active' : ''}`}
                onClick={() => {
                  setStatusFilter(status === 'All' ? '' : status);
                  setPage(1);
                }}
              >
                {status}
              </button>
            ))}
          </div>

          <div className="flex-spacer" />
 
          <div className="search-wrapper">
            <Search size={18} className="search-icon-left" />
            <input
              type="text"
              className="search-input search-input-with-icon"
              placeholder="Search clients or contacts"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(1);
              }}
            />
          </div>
        </div>

        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: '40px' }}>
                  <input
                    type="checkbox"
                    onChange={handleSelectAll}
                    checked={clients.length > 0 && selectedIds.length === clients.length}
                    ref={input => {
                      if (input) {
                        input.indeterminate = selectedIds.length > 0 && selectedIds.length < clients.length;
                      }
                    }}
                  />
                </th>
                <th>Client</th>
                <th>Company</th>
                <th>Industry</th>
                <th>Status</th>
                <th>Projects</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {clients.map((client) => (
                <tr key={client.id}>
                  <td style={{ width: '40px' }}>
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(client.id)}
                      onChange={(e) => handleSelectOne(client.id, e.target.checked)}
                    />
                  </td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div
                        className="avatar avatar-md"
                        style={{ background: getAvatarColor(client.name) }}
                      >
                        {getInitials(client.name)}
                      </div>
                      <div>
                        <div style={{ fontWeight: 500 }}>{client.name}</div>
                        <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>{client.email}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div>{client.company}</div>
                    <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>{client.country}</div>
                  </td>
                  <td>{client.industry}</td>
                  <td>
                    <span 
                      className="badge"
                      style={{
                        backgroundColor: `${getStatusColor(client.status)}15`,
                        color: getStatusColor(client.status),
                        border: `1px solid ${getStatusColor(client.status)}25`
                      }}
                    >
                      {client.status}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Folder size={14} style={{ color: 'var(--success)' }} />
                        {client.projectCount || 0}
                      </span>
                    </div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      <button className="btn-icon" onClick={() => openEditModal(client)}>
                        <Edit2 size={16} />
                      </button>
                      <button className="btn-icon" onClick={() => handleDelete(client.id)}>
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {clients.length === 0 && !loading && (
            <div className="empty-state">
              <h3>No clients found</h3>
              <p>Create your first client to get started</p>
            </div>
          )}

          {totalPages > 1 && (
            <div className="pagination">
              <div className="pagination-info">
                Page {page} of {totalPages} ({total} total)
              </div>
              <div className="pagination-buttons">
                <button
                  className="pagination-btn"
                  disabled={page === 1}
                  onClick={() => setPage(p => p - 1)}
                >
                  Previous
                </button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => i + 1).map(p => (
                  <button
                    key={p}
                    className={`pagination-btn ${p === page ? 'active' : ''}`}
                    onClick={() => setPage(p)}
                  >
                    {p}
                  </button>
                ))}
                <button
                  className="pagination-btn"
                  disabled={page === totalPages}
                  onClick={() => setPage(p => p + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingClient ? 'Edit Client' : 'New Client'}</h2>
              <button className="btn-icon" onClick={closeModal}>
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Contact Name *</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Email *</label>
                    <input
                      type="email"
                      className="form-input"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Company *</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Country</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.country}
                      onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Industry</label>
                    <select
                      className="form-select"
                      value={formData.industry}
                      onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                    >
                      <option value="">Select industry</option>
                      {industriesData?.catalogs?.map((ind: Catalog) => (
                        <option key={ind.id} value={ind.name}>{ind.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Status</label>
                    <select
                      className="form-select"
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    >
                      {statusesData?.catalogs?.map(status => (
                        <option key={status.id} value={status.name}>{status.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeModal}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingClient ? 'Save Changes' : 'Create Client'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

export default Clients;
